'use client';

import { useForm, type DefaultValues, type FieldValues, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ReactNode, JSX } from 'react';
import { Button } from '../buttons/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './';
import type { FormFieldMapping } from '../../types/FormFieldMapping';
import { fieldRegistry } from '../../types/fieldRegistry';
import { Logger } from '@shared/logger';
import { UI_MESSAGES, UI_RESOURCES } from '@shared/constants';
import { tryCatchAsync } from '@shared/utils';

function transformNestedData(
  data: Record<string, any>,
  transform: (key: string, value: any) => any
): Record<string, any> {
  const result: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Handle nested objects
      result[key] = transformNestedData(value, transform);
    } else if (Array.isArray(value)) {
      // Handle arrays
      result[key] = value.map(item => 
        typeof item === 'object' && item !== null 
          ? transformNestedData(item, transform)
          : transform(key, item)
      );
    } else {
      // Handle primitive values
      result[key] = transform(key, value);
    }
  }
  
  return result;
}

interface DynamicFormProps<T extends FieldValues> {
  mappings: Record<string, FormFieldMapping>;
  onSubmit: (data: T) => Promise<void> | void;
  defaultValues?: DefaultValues<T>;
  schema: z.ZodType<T>;
  children?: ReactNode;
  isEdit?: boolean;
  onCancel?: () => void;
  relatedData?: Record<string, any[]>;
  isLoading?: boolean;
}

export function DynamicForm<T extends FieldValues>({
  mappings,
  onSubmit,
  defaultValues,
  schema,
  children,
  isEdit = false,
  onCancel,
  relatedData = {},
  isLoading = false,
}: DynamicFormProps<T>): JSX.Element {
  const form = useForm<T>({
    defaultValues,
    resolver: isEdit ? undefined : zodResolver(schema), // Skip schema validation for edit mode
    mode: 'onChange'
  });

  const handleSubmit = async (data: T): Promise<void> => {
    const result = await tryCatchAsync(async () => {
      // Filter out hidden fields and system-managed fields from the submitted data
      const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
        const mapping = mappings[key];
        // Skip hidden fields in submission
        if (mapping && (mapping.hidden || mapping.hideFromForm || (mapping.showOnlyOnCreate && isEdit))) {
          return acc;
        }
        // Skip system-managed fields - these should be handled automatically by the repository
        if (key === 'id' || key === 'external_id' || 
            key === 'date_created' || key === 'date_modified' || 
            key === 'createdAt' || key === 'updatedAt' || 
            key === 'created_at' || key === 'updated_at') {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {} as Record<string, any>);

      // Transform the data, handling nested fields and custom transformations
      const transformedData = transformNestedData(filteredData, (key, value) => {
        const mapping = mappings[key];
        if (mapping?.transform) {
          return mapping.transform(value);
        }
        return value ?? null; // Convert undefined to null for consistency
      }) as T;

      await onSubmit(transformedData);
    });

    if (result.isErr) {
      const error = result.getFail();
      Logger.error(UI_MESSAGES.ERROR.FORM_SUBMISSION_ERROR, { resource: UI_RESOURCES.DASHBOARD }, error instanceof Error ? error : new Error(String(error)));
    }
  };

  const renderField = (fieldName: string, mapping: FormFieldMapping) => {
    if (mapping.hidden) return null;
    if (mapping.hideFromForm) return null;
    if (mapping.showOnlyOnCreate && isEdit) return null;

    const Component = mapping.component || fieldRegistry[mapping.type || 'text'];
    if (!Component) {
              Logger.warn(`No component found for field type: ${mapping.type}`, { resource: UI_RESOURCES.DASHBOARD });
      return null;
    }

    const { defaultValue, ...rawComponentProps } = mapping.props || {};
    
    // Resolve function props - only call functions that are meant to be data providers
    const componentProps = Object.entries(rawComponentProps).reduce((acc, [key, value]) => {
      if (typeof value === 'function' && (key === 'data' || key === 'isLoading')) {
        acc[key] = value();
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    // Add related data for components that need it
    if (mapping.relatedData) {
      componentProps.relatedData = mapping.relatedData.reduce((acc, config) => {
        acc[config.key] = relatedData[config.key] || [];
        return acc;
      }, {} as Record<string, any[]>);
    }

    return (
      <FormField
        key={fieldName}
        control={form.control}
        name={fieldName as Path<T>}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              {mapping.label}
              {(typeof mapping.required === 'function' ? mapping.required(isEdit) : mapping.required) && (
                <span className="text-red-500">*</span>
              )}
            </FormLabel>
            <FormControl>
              <Component
                {...field}
                {...componentProps}
                placeholder={mapping.placeholder}
                value={field.value ?? ''}
                onChange={(e: any) => {
                  const value = e?.target?.value ?? e;
                  field.onChange(value);
                  mapping.onChange?.(value);
                }}
                onBlur={() => {
                  field.onBlur();
                  mapping.onBlur?.();
                }}
                aria-invalid={fieldState.invalid}
                disabled={form.formState.isSubmitting || mapping.disabled}
                // Additional props for relationship components only
                {...(mapping.relatedData ? {
                  itemId: defaultValues?.id || 'new',
                  currentItems: field.value || [],
                  onDataChange: (newData: any) => {
                    field.onChange(newData);
                    mapping.onChange?.(newData);
                  },
                  isLoading: isLoading
                } : {})}
              />
            </FormControl>
            {mapping.description && (
              <FormDescription>{mapping.description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  // Check if the form is valid and has all required fields filled
  const isFormValid = isEdit || form.formState.isValid; // For edit mode, skip schema validation
  const hasRequiredFields = Object.entries(mappings).every(([fieldName, mapping]) => {
    // Skip validation for hidden fields
    if (mapping.hidden || mapping.hideFromForm || (mapping.showOnlyOnCreate && isEdit)) {
      return true;
    }
    
    const isRequired = typeof mapping.required === 'function' ? mapping.required(isEdit) : mapping.required;
    if (!isRequired) return true;
    const value = form.getValues(fieldName as Path<T>);
    return value !== undefined && value !== null && value !== '';
  });

  return (
    <Form {...form}>
      <form 
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        {Object.entries(mappings).map(([fieldName, mapping]) => 
          renderField(fieldName, mapping)
        )}
        {children}
        <div className="flex justify-end gap-2 mt-4">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={form.formState.isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={form.formState.isSubmitting || !isFormValid || (!isEdit && !hasRequiredFields)}
          >
            {form.formState.isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  );
} 